import React, { MouseEvent, useCallback, useState, ReactNode } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Box } from 'transfix-ui/core'
import { IShipmentDocument } from 'types'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { IFileCreate, TFileType, IFile, IBaseFileType } from 'apps/fleet-planner/types'
import {
  uploadAttachmentSuccess,
  uploadAttachmentFailure,
  deleteAttachmentSuccess,
  updateLoadStatus,
} from 'apps/fleet-planner/utils/actions/loadDetails'
import { getMpDocuments, getFpDocuments } from 'apps/fleet-planner/utils/api'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { Attachments } from '../form-components/Attachments'
import { DocumentPodList } from './DocumentPodList'
import { DocumentRateConList } from './DocumentRateConList'
import { DocumentScaleTicketList } from './DocumentScaleTicketList'

/**
 * Groups logic for displaying documents on Load Details Modal/Page
 */
export const LoadDetailsDocuments = () => {
  const { trackEvent } = useFMSAnalytics()
  const dispatch = useDispatch()
  const [loadingTypes, setLoadingTypes] = useState<Record<TFileType, boolean>>({
    pod: false,
    scale_ticket: false,
    misc: false,
    rate_con: false,
    payable: false,
    private: false,
  })

  const [isPrivateDocumentLoading, setIsPrivateDocumentLoading] = useState<boolean>(false)

  const setLoading = (keyName: TFileType, isLoading: boolean) => {
    setLoadingTypes(prev => ({
      ...prev,
      [keyName]: isLoading,
    }))
  }

  const {
    addAttachments,
    deleteAttachment,
    replaceAttachment,
    shipmentDetails,
  } = useMarketplaceContext()
  const { documents: mpDocuments = [] } = shipmentDetails || {}

  const rejectedDocuments =
    mpDocuments.filter(({ key }) => key === 'pod_rejected') || ([] as IShipmentDocument[])

  const { load } = useSelector(loadDetailsSelector)
  const { attachments: fpAttachments = [], pods: fpPods = [], isExternal, status, podState } =
    load || {}

  const { getFlag } = useFeatureFlagContext()
  const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')
  const { enabled: isFmsRejectionReasonPOD } = getFlag('fmsRejectionReasonPOD')

  const documentChangeHandler = useCallback(
    async (files: IFileCreate[], fileType: TFileType) => {
      setLoading(fileType, true)
      const response = await addAttachments(load, files, fileType)
      if (fileType === 'pod') trackEvent('POD Upload', 'POD_UPLOADED_DISPATCHER_VIEW')
      const { id } = response || {}

      // Update `loadDetails` reducer in Redux
      if (id) {
        dispatch(uploadAttachmentSuccess(response))
        // If shipment status is POD rejected, transtion the status to Payment Pending after a successful upload.
        if (status === LOAD_STATE['POD Rejected']) {
          dispatch(updateLoadStatus(load, LOAD_STATE['Payment Pending'], undefined))
        }
      } else {
        dispatch(uploadAttachmentFailure(response))
      }

      setLoading(fileType, false)
    },
    [addAttachments, dispatch, load]
  )

  const documentReplaceHandler = useCallback(
    async (replacedUrl: string, newFiles: IFile[], fileType: TFileType) => {
      setLoading(fileType, true)
      const response = await replaceAttachment(replacedUrl, load, newFiles, fileType)
      const { id } = response || {}

      // Update `loadDetails` reducer in Redux
      if (id) {
        dispatch(uploadAttachmentSuccess(response))
      } else {
        dispatch(uploadAttachmentFailure(response))
      }

      setLoading(fileType, false)
    },
    [dispatch, load, replaceAttachment]
  )

  const deleteFileHandler = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      if (event?.currentTarget.id) {
        setIsPrivateDocumentLoading(true)
        const attachmentId = parseInt(event?.currentTarget.id, 10)
        const response = await deleteAttachment(attachmentId, load)
        const { id } = response || {}

        // Update `loadDetails` reducer in Redux
        if (id) {
          dispatch(deleteAttachmentSuccess(response))
        }
      }
      setIsPrivateDocumentLoading(false)
    },
    [deleteAttachment, dispatch, load]
  )

  const getRateConSubtitle = (document: IBaseFileType): ReactNode => {
    const { info } = shipmentDetails || {}
    const { carrierSignedAt = '' } = info || {}
    const { createdAt = moment().format() } = document || {}

    /**
     * `carrierSignedAt` has a timestamp with time and offset, so
     * we can style it a bit differently.
     */
    if (document.key === 'rate_confirmation') {
      return (
        <>
          Signed{' '}
          <FormattedDateTime
            inSentence
            showTimezone
            datetime={carrierSignedAt}
            format='timeDateAndYear'
            displayTimezone={moment.tz.guess() as AllowedTimezones}
          />
        </>
      )
    }

    /**
     * If the document is not a Rate Con, use the more generic format,
     * which excludes the time since that info isn't available
     */
    return (
      <>
        Added{' '}
        <FormattedDateTime
          inSentence
          showTimezone={false}
          datetime={createdAt}
          format='dateAndYear'
          displayTimezone={moment.tz.guess() as AllowedTimezones}
        />
      </>
    )
  }

  return (
    <>
      {/**
       * Enabled: fpCarrierPortal
       */}
      {isFpCarrierPortalEnabled && isExternal ? (
        <Box pt={1}>
          <Box mb={3}>
            <DocumentRateConList
              isLoading={loadingTypes.rate_con}
              documents={getMpDocuments({
                documents: mpDocuments,
                fileTypes: ['rate_confirmation', 'payable'],
              })}
              onChange={documentChangeHandler}
              onReplace={documentReplaceHandler}
              getDocumentSubtitle={getRateConSubtitle}
            />
          </Box>
          <Box mb={3}>
            <DocumentPodList
              status={status}
              isLoading={loadingTypes.pod}
              documents={getFpDocuments({
                documents: fpPods,
              })}
              podState={podState}
              rejectedDocuments={isFmsRejectionReasonPOD ? rejectedDocuments : []}
              onChange={documentChangeHandler}
              onReplace={documentReplaceHandler}
            />
          </Box>
          <Box mb={3}>
            <DocumentScaleTicketList
              isLoading={loadingTypes.scale_ticket || loadingTypes.misc}
              documents={getMpDocuments({
                documents: mpDocuments,
                fileTypes: ['ScaleTicket', 'invoice_file_name', 'misc'],
              })}
              onReplace={documentReplaceHandler}
              onChange={documentChangeHandler}
            />
          </Box>
          <Box pb={6}>
            <Attachments
              isLoading={isPrivateDocumentLoading}
              fileType='private'
              dropzoneMessage='Upload a file or drop it here.'
              isExternal={isExternal}
              onFileChange={documentChangeHandler}
              onFileDelete={deleteFileHandler}
              headerText='Private Documents'
              files={fpAttachments}
            />
          </Box>
        </Box>
      ) : (
        <>
          {/**
           * Disabled: fpCarrierPortal
           */}
          <div className='mt5'>
            <Attachments
              showIcon={false}
              fileType='pod'
              dropzoneMessage='Click to upload your POD, or drag and drop files here.'
              isExternal={isExternal}
              onFileChange={documentChangeHandler}
              onFileDelete={deleteFileHandler}
              headerText='POD'
              files={fpPods}
            />
          </div>
          <div className='mt5'>
            <Attachments
              showIcon={false}
              fileType='private'
              dropzoneMessage='Upload a file or drop it here.'
              isExternal={isExternal}
              onFileChange={documentChangeHandler}
              onFileDelete={deleteFileHandler}
              headerText='Other Attachments'
              files={fpAttachments}
            />
          </div>
        </>
      )}
    </>
  )
}
